import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWpPostsByCategory } from '../../actions/learn';
import imgSlider1 from '../../images/imgslider1.jpg'
import imgSlider2 from '../../images/imgslider2.jpg'

const CarouselSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  
  // Get customer stories from Redux store
  const customerStories = useSelector(state => state.wpPosts.toJS());
  const wpPostsError = useSelector(state => state.wpPostsError);
  
  // Touch/Swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Fetch customer stories on component mount using Redux action
  useEffect(() => {
    dispatch(fetchWpPostsByCategory('customer-story', 1, 4));
  }, [dispatch]);

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

  // Convert customer stories to cards format
  const cards = customerStories.slice(0, 4).map((post, index) => ({
    id: post.id || index + 1,
    image: post.thumbnail || (index % 2 === 0 ? imgSlider1 : imgSlider2),
    title: post.title || 'Customer Story',
    content: post.short_description || 'Discover how our platform transforms talent acquisition and management processes.',
    category: post.categories && post.categories.length > 0 
      ? decodeHtmlEntities(post.categories[0]) 
      : 'Customer Story',
    date: post.date,
    readDuration: post.read_duration
  }));

  // Handle errors from Redux
  useEffect(() => {
    if (wpPostsError) {
      console.error('Error fetching customer stories:', wpPostsError);
    }
  }, [wpPostsError]);

  const displayCards = cards;

  // Create extended array for seamless looping
  const extendedCards = [...displayCards, ...displayCards, ...displayCards];
  const totalCards = displayCards.length;

  // Minimum distance to trigger swipe (in pixels)
  const minSwipeDistance = 50;

  // Start from the middle set to enable seamless looping
  useEffect(() => {
    setCurrentIndex(totalCards);
  }, [totalCards]);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1); // Move left to right (decrease index)
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1); // Move right to left (increase index)
  };

  // Touch event handlers
  const onTouchStart = (e) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide(); // Swipe left = next slide
    } else if (isRightSwipe) {
      prevSlide(); // Swipe right = previous slide
    }
  };

  // Handle card click to navigate to learn-detail
  const handleCardClick = (cardId) => {
    history.push(`/learn-detail/${cardId}`);
  };

  // Handle seamless loop reset
  const handleTransitionEnd = () => {
    setIsTransitioning(false);
    if (currentIndex <= 0) {
      setCurrentIndex(totalCards);
    } else if (currentIndex > totalCards * 2) {
      setCurrentIndex(totalCards + 1);
    }
  };

  return (
    <div className="carousel-container">
      <div className="carousel-wrapper">
        {/* Carousel Container */}
        <div 
          className={`carousel-track ${!isTransitioning ? 'no-transition' : ''}`}
          style={{
            transform: `translateX(calc(-${currentIndex} * var(--slide-width)))`
          }}
          onTransitionEnd={handleTransitionEnd}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {extendedCards.map((card, index) => {
            // Determine if this card is the active (center) card
            const isActive = index === currentIndex;
            
            return (
              <div
                key={`${card.id}-${Math.floor(index / totalCards)}`}
                className={`carousel-slide ${isActive ? 'active' : 'inactive'} ${!isTransitioning ? 'no-transition' : ''}`}
              >
                <div 
                  className="card"
                  onClick={() => handleCardClick(card.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <img className="card-image" src={card.image} alt={card.title} />
                  <h3 className="card-title">{card.title}</h3>
                  <p className="card-content">{card.content}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        <div className="nav-controls">
          <button
            onClick={prevSlide}
            disabled={isTransitioning}
            className="nav-button"
            aria-label="Previous slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </button>
          
          <button
            onClick={nextSlide}
            disabled={isTransitioning}
            className="nav-button"
            aria-label="Next slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9,6 15,12 9,18"></polyline>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CarouselSlider;